import { useState } from "react";
import { Button } from "src/components";
import { useToast } from "src/hooks/useToast";
import { useEventStatus } from "src/hooks/useEventStatus";
import { revertCounterPo } from "src/services/order-api";

// COUNTER-PO-REVERT ==========================================================

const PoRevertModal = ({
  stockistPoAttachmentId,
  companyCode,
  onCloseModal,
  onSuccess,
}: {
  stockistPoAttachmentId: number | string;
  companyCode?: number | string;
  onCloseModal: () => void;
  onSuccess?: () => void;
}) => {
  const { toast } = useToast();
  const { withEventCheck } = useEventStatus();
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRevert = async () => {
    setIsSubmitting(true);
    try {
      const res = await revertCounterPo(
        {
          event_type: "COUNTER_PO_REVERT",
          primary_key: stockistPoAttachmentId,
          data: {
            stockist_po_attachment_id: stockistPoAttachmentId,
            revert_remarks: remarks.trim() || undefined,
          },
        },
        companyCode,
      );
      if (res?.status !== 200 && res?.status !== 201) {
        // Backend throws here when a linked order exists and isn't Cancelled yet
        throw new Error(res?.data?.message || "Failed to revert PO");
      }
      // Event-sourced write — block on consumption before treating this as done.
      const streamId = res?.data?.data?.eventStreamCreated?.streamId;
      await withEventCheck(() => {}, streamId, Number(companyCode));
      toast({ description: "PO reverted to Pending successfully", variant: "default" });
      onSuccess?.();
      onCloseModal();
    } catch (error: any) {
      toast({
        description: error?.response?.data?.message || error?.message || "Failed to revert PO",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-y-3 w-[480px] mx-auto bg-white p-5 border border-gray-300 shadow-lg rounded-lg">
      <div>
        <p className="text-base font-semibold text-[#1B1A1F]">Revert PO</p>
        <p className="text-xs text-[#666666]">
          Restores this PO to Pending. Blocked if a linked order exists and hasn't been cancelled.
        </p>
      </div>

      <div>
        <p className="text-sm text-[#333] mb-1">Remarks (optional)</p>
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Add any additional remarks"
          rows={3}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-[#f7f7f7] focus:outline-none focus:ring-1 focus:ring-[#8051FF]"
        />
      </div>

      <div className="flex gap-2 mt-1">
        <Button className="flex-1 bg-gray-100 text-[#333] hover:bg-gray-200" onClick={onCloseModal}>
          Cancel
        </Button>
        <Button
          disabled={isSubmitting}
          isLoading={isSubmitting}
          loaderSize="small"
          className="flex-1 text-white bg-[#8051FF] hover:bg-[#6f3fe0]"
          onClick={handleRevert}
        >
          Confirm Revert
        </Button>
      </div>
    </div>
  );
};

export default PoRevertModal;
// COUNTER-PO-REVERT ==========================================================
